import { useEffect, useState } from 'react';
import type { Poll } from '@gl/shared';

import { useDeletePoll, useEndPoll, useVote } from '../api/hooks.js';
import { useCan, useCurrentUser } from '../auth.js';
import { formatDate } from '../util/format.js';
import { PollEditor } from './PollEditor.js';

export function PollCard({ poll }: { poll: Poll }) {
  const canEdit = useCan('edit_polls');
  const me = useCurrentUser()?.id ?? null;
  const vote = useVote();
  const endPoll = useEndPoll();
  const deletePoll = useDeletePoll();

  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState<string[]>(poll.myVotes);
  const ended = poll.endedAt !== null;

  // Server state wins whenever the poll reloads (someone else edited it, or our
  // own vote round-tripped).
  useEffect(() => setSelected(poll.myVotes), [poll.myVotes]);

  const dirty =
    selected.length !== poll.myVotes.length || selected.some((id) => !poll.myVotes.includes(id));

  const toggle = (optionId: string) => {
    if (ended) return;
    setSelected((current) =>
      poll.multiSelect
        ? current.includes(optionId)
          ? current.filter((id) => id !== optionId)
          : [...current, optionId]
        : current.includes(optionId)
          ? []
          : [optionId],
    );
  };

  return (
    <article className="card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-2xl font-bold uppercase leading-none tracking-tight text-field-50">
            {poll.question}
          </h3>
          {poll.description ? (
            <p className="mt-1 text-sm text-field-300">{poll.description}</p>
          ) : null}
          <p className="mt-2 font-display text-xs font-semibold uppercase tracking-bug text-field-500">
            {poll.multiSelect ? 'Pick as many as you like' : 'Pick one'} · {poll.totalVoters}{' '}
            {poll.totalVoters === 1 ? 'voter' : 'voters'}
            {ended && poll.endedAt ? ` · ended ${formatDate(poll.endedAt)}` : ''}
          </p>
        </div>
        {ended ? <span className="badge shrink-0 bg-field-700 text-field-100">Closed</span> : null}
      </div>

      <ul className="mt-4 space-y-2">
        {poll.options.map((option) => {
          const chosen = selected.includes(option.id);
          return (
            <li key={option.id}>
              <button
                type="button"
                disabled={ended}
                onClick={() => toggle(option.id)}
                aria-pressed={chosen}
                className={`relative w-full overflow-hidden border px-3 py-2 text-left
                  transition-colors ${
                    chosen
                      ? 'border-brass-400 bg-field-950'
                      : 'border-field-700 bg-field-950 hover:border-field-500'
                  } ${ended ? 'cursor-default' : ''}`}
              >
                {/* Result bar sits behind the label rather than beside it. */}
                <span
                  aria-hidden
                  className={`absolute inset-y-0 left-0 ${chosen ? 'bg-brass-400/25' : 'bg-field-700/60'}`}
                  style={{ width: `${option.votePercent}%` }}
                />
                <span className="relative flex items-center justify-between gap-3">
                  <span className="font-display text-base font-semibold uppercase tracking-tight text-field-50">
                    {option.text}
                  </span>
                  <span className="shrink-0 whitespace-nowrap">
                    <span className="font-display text-xl font-bold tabular-nums leading-none text-brass-400">
                      {option.votePercent.toFixed(1)}%
                    </span>
                    <span className="ml-2 font-display text-xs uppercase tracking-bug text-field-500">
                      {option.voteCount}
                    </span>
                  </span>
                </span>
              </button>

              {option.voters.length > 0 ? (
                <p className="mb-1 mt-1 pl-3 text-xs text-field-500">
                  {/* Your own name stands out, so who voted for what is readable at a glance. */}
                  {option.voters.map((voter, index) => (
                    <span key={voter.userId}>
                      {index > 0 ? ', ' : ''}
                      <span
                        className={voter.userId === me ? 'font-semibold text-brass-300' : undefined}
                      >
                        {voter.displayName}
                        {voter.userId === me ? ' (you)' : ''}
                      </span>
                    </span>
                  ))}
                </p>
              ) : null}
            </li>
          );
        })}
      </ul>

      {poll.nonVoters.length > 0 ? (
        <div className="mt-4 border-t border-field-800 pt-3">
          <p className="font-display text-xs font-semibold uppercase tracking-bug text-field-500">
            {ended ? 'Never voted' : 'Still to vote'} · {poll.nonVoters.length}
          </p>
          <p className="mt-1 text-xs text-field-500">
            {/* Same treatment as the voter lists above: your own name stands out. */}
            {poll.nonVoters.map((member, index) => (
              <span key={member.userId}>
                {index > 0 ? ', ' : ''}
                <span className={member.userId === me ? 'font-semibold text-brass-300' : undefined}>
                  {member.displayName}
                  {member.userId === me ? ' (you)' : ''}
                </span>
              </span>
            ))}
          </p>
        </div>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {!ended ? (
          <button
            type="button"
            className="btn-primary"
            disabled={!dirty || vote.isPending}
            onClick={() => vote.mutate({ pollId: poll.id, optionIds: selected })}
          >
            {vote.isPending ? 'Saving…' : poll.myVotes.length > 0 ? 'Change vote' : 'Vote'}
          </button>
        ) : null}

        {dirty && !ended ? (
          <button type="button" className="btn-ghost" onClick={() => setSelected(poll.myVotes)}>
            Reset
          </button>
        ) : null}

        {canEdit ? (
          <div className="ml-auto flex gap-2">
            <button type="button" className="btn-secondary" onClick={() => setEditing(true)}>
              Edit
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => endPoll.mutate({ id: poll.id, reopen: ended })}
            >
              {ended ? 'Reopen' : 'End poll'}
            </button>
            <button
              type="button"
              className="btn-danger"
              onClick={() => {
                if (confirm(`Delete "${poll.question}" and all of its votes?`)) {
                  deletePoll.mutate(poll.id);
                }
              }}
            >
              Delete
            </button>
          </div>
        ) : null}
      </div>

      {vote.error ? (
        <p className="mt-2 text-sm text-signal">{(vote.error as Error).message}</p>
      ) : null}

      {editing ? <PollEditor poll={poll} onClose={() => setEditing(false)} /> : null}
    </article>
  );
}
